import { Fund, RankingMetric } from "creso-core/types";
import {
  FUND_METRIC_FORMATTER,
  FUND_METRICS_GLOSSARY,
  getRankingMetricLabel,
} from "creso-core/constants";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "web-design-system";
import { ChevronDown, Star } from "lucide-react";
import { InsightLogo } from "@core/components/ai-logo";
import { parseScore } from "@funds/utils";
import { get } from "lodash";
import { FUND_SCORE_CONFIG } from "@funds/config";

const DEFAULT_METRIC_VALUE = "—";

function formatMetricValue(metric: RankingMetric, val?: string) {
  if (!val) return DEFAULT_METRIC_VALUE;
  const formatter =
    FUND_METRIC_FORMATTER?.[metric as keyof typeof FUND_METRIC_FORMATTER];
  return formatter ? formatter(val) : parseFloat(val).toFixed(2);
}

function FundScoreDetails({
  funds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  return (
    <div
      className={cn(
        "bg-background border-border",
        variant === "report" ? "border-t-0" : "border-t"
      )}
    >
      {FUND_SCORE_CONFIG.map((section) => (
        <div key={section.id} className="border-b border-border">
          <div className="flex border-b border-border">
            <div
              className="px-5 py-3 shrink-0 bg-muted text-sm items-center flex font-semibold"
              style={{ width: labelWidth }}
            >
              {section.title}
            </div>
            {funds.map((fund, idx) => {
              const { ranking } = fund;
              return (
                <div
                  key={fund.fund_id}
                  className={cn(
                    "flex flex-1 px-5 py-3 items-center",
                    idx < funds.length - 1 && "border-r border-border"
                  )}
                >
                  {ranking && ranking[section.metricsKey].length ? (
                    <span className="text-sm font-semibold px-2 py-0.5 bg-secondary rounded-md">
                      {parseScore(ranking[section.scoreKey])}
                      <span className="font-normal text-xs text-muted-foreground">
                        &nbsp;/ 5
                      </span>
                    </span>
                  ) : (
                    <span className="text-sm">N/A</span>
                  )}
                </div>
              );
            })}
            {variant === "default" && <div className="w-[80px] shrink-0" />}
          </div>

          {Array.from(
            new Set(
              funds.flatMap((fund) => fund.ranking?.[section.metricsKey] ?? [])
            )
          ).map((metric) => (
            <div
              key={metric}
              className="flex items-center border-b border-border last:border-b-0"
            >
              <div
                className="px-5 py-3 shrink-0 bg-muted"
                style={{ width: labelWidth }}
              >
                <Tooltip>
                  <TooltipTrigger className="text-left text-sm ">
                    {getRankingMetricLabel(metric) ?? metric}
                  </TooltipTrigger>
                  <TooltipContent className="max-w-60">
                    {FUND_METRICS_GLOSSARY[metric]}
                  </TooltipContent>
                </Tooltip>
              </div>
              {funds.map((fund, idx) => {
                const { ranking, performance } = fund;
                let value = DEFAULT_METRIC_VALUE;
                if (ranking) {
                  value = formatMetricValue(metric, get(ranking, metric));
                }
                if (value === DEFAULT_METRIC_VALUE) {
                  value = formatMetricValue(metric, get(performance, metric));
                }
                if (value === DEFAULT_METRIC_VALUE) {
                  value = formatMetricValue(metric, get(fund, metric));
                }
                return (
                  <div
                    key={fund.fund_id}
                    className={cn(
                      "flex flex-1 px-5 py-3",
                      idx < funds.length - 1 && "border-r border-border"
                    )}
                  >
                    <span className="text-sm">{value}</span>
                  </div>
                );
              })}
              {variant === "default" && (
                <div className="w-[80px] shrink-0 bg-background px-5 py-3" />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function FundScore({
  funds,
  labelWidth,
  variant = "default",
}: {
  funds: Fund[];
  labelWidth: number;
  variant?: "default" | "report";
}) {
  const scoreCells = (
    <div className="flex flex-1">
      {funds.map((fund) => {
        const { ranking } = fund;
        return (
          <div
            key={fund.fund_id}
            className={cn("flex flex-1 items-center px-5 gap-1.5")}
          >
            {ranking ? (
              <>
                <span className="text-base font-semibold flex gap-1 items-center bg-secondary rounded px-2 py-0.5">
                  {parseScore(ranking.total_score)}
                  <Star className="size-4 fill-chart-3 stroke-chart-3" />
                </span>
              </>
            ) : (
              <span className="text-sm ">N/A</span>
            )}
          </div>
        );
      })}
    </div>
  );

  const summaryHeader = (
    <>
      <span
        className="font-semibold text-base px-5 text-left shrink-0"
        style={{ width: labelWidth }}
      >
        Fund Score
      </span>
      {scoreCells}
      {variant === "default" && <div className="w-[80px] shrink-0" />}
    </>
  );

  return (
    <div className="flex flex-col break-inside-avoid">
      <Collapsible
        defaultOpen
        className="w-full bg-muted rounded-lg overflow-hidden border border-border group"
      >
        <CollapsibleTrigger
          className={cn(
            "flex relative w-full items-center py-4",
            variant === "report" && "pointer-events-none"
          )}
        >
          {summaryHeader}
          <div className="absolute flex right-5 gap-2 items-center">
            <InsightLogo />
            {variant === "default" ? (
              <ChevronDown className="w-4 h-4 transition-transform duration-300 group-data-[state=open]:rotate-180 text-muted-foreground" />
            ) : null}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <FundScoreDetails
            variant={variant}
            funds={funds}
            labelWidth={labelWidth}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
